import { TrashIcon } from '@/utils/helpers';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

type LocalCharacteristic = {
  rowId: string;
  id?: string;
  enDescription: string;
  daDescription: string;
};

type PhishingCharacteristicsManagementTabProps = {
  characteristics: LocalCharacteristic[];
  isLoading: boolean;
  isSaving: boolean;
  onAddRow: () => void;
  onDeleteRow: (rowId: string) => void;
  onChange: (
    rowId: string,
    field: 'enDescription' | 'daDescription',
    value: string,
  ) => void;
};

const StyledList = styled(Card)`
  &.p-card {
    border-radius: var(--medium-border-radius);
    border: 1px solid var(--gray-dark);
    box-shadow: none;

    .p-card-body {
      padding: var(--big-padding);
    }

    .p-card-content {
      display: flex;
      flex-direction: column;
      gap: var(--default-padding);
      padding: 0;
    }
  }
`;

const CharacteristicsTable = styled.div`
  background-color: var(--beige-main);
  border-radius: var(--small-border-radius);
  padding: var(--medium-big-padding);
  display: flex;
  flex-direction: column;
  gap: var(--default-padding-small);
`;

const ColumnHeaders = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr var(--default-padding);
  gap: var(--default-padding);

  span {
    font-weight: 600;
    font-size: var(--medium-font-size);
    letter-spacing: 0.32px;
    color: var(--black-main);
  }
`;

const StyledRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr var(--default-padding);
  align-items: center;
  gap: var(--default-padding);

  .p-inputtext {
    background-color: var(--white-main);
    border-radius: var(--default-border-radius);
    border: 1px solid var(--gray-dark);
    padding: 13px var(--medium-padding);
  }
`;

const DeleteButton = styled.button`
  all: unset;
  color: var(--black-main);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    path {
      fill: var(--red-main);
    }
    color: var(--red-main);
  }

  &:disabled {
    opacity: 0.5;
    pointer-events: none;
    cursor: not-allowed;
  }
`;

const AddRowButton = styled.button<{ disabled?: boolean }>`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--semi-xsmall-padding);
  width: 100%;
  padding: var(--small-padding);
  border: 1px solid var(--gray-dark);
  border-radius: var(--xsmall-border-radius);
  cursor: pointer;
  color: var(--gray-darker);
  font-size: var(--small-font-size);
  background-color: transparent;
  transition: color 0.2s ease-in-out, border-color 0.2s ease-in-out;

  &:hover {
    border-color: var(--gray-darker);
    color: var(--black-main);
  }

  &:disabled {
    opacity: 0.5;
    pointer-events: none;
    cursor: not-allowed;
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: var(--big-padding);
`;

export const PhishingCharacteristicsManagementTab: React.FC<
  PhishingCharacteristicsManagementTabProps
> = ({
  characteristics,
  isLoading,
  isSaving,
  onAddRow,
  onDeleteRow,
  onChange,
}) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <LoadingWrapper>
        <ProgressSpinner />
      </LoadingWrapper>
    );
  }

  return (
    <StyledList>
      <CharacteristicsTable>
        <ColumnHeaders>
          <span>{t('common.english')}</span>
          <span>{t('common.danish')}</span>
          <span />
        </ColumnHeaders>
        {characteristics.map((characteristic) => (
          <StyledRow key={characteristic.rowId}>
            <InputText
              value={characteristic.enDescription}
              onChange={(e) =>
                onChange(characteristic.rowId, 'enDescription', e.target.value)
              }
              placeholder={t('globalSettings.characteristicEnglish')}
              className="w-full"
            />
            <InputText
              value={characteristic.daDescription}
              onChange={(e) =>
                onChange(characteristic.rowId, 'daDescription', e.target.value)
              }
              placeholder={t('globalSettings.characteristicDanish')}
              className="w-full"
            />
            <DeleteButton
              type="button"
              disabled={isSaving}
              onClick={() => onDeleteRow(characteristic.rowId)}
              aria-label={t('button.delete')}
            >
              <TrashIcon />
            </DeleteButton>
          </StyledRow>
        ))}

        <AddRowButton type="button" disabled={isSaving} onClick={onAddRow}>
          <i className="pi pi-plus" />
          {t('globalSettings.addCharacteristic')}
        </AddRowButton>
      </CharacteristicsTable>
    </StyledList>
  );
};
